import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import logo from '../assets/logo.png';

export const exportToExcel = (data, filename, sheetName = 'Sheet1', title = '') => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);
  
  // Set column widths
  const maxWidths = [];
  if (data.length > 0) {
    Object.keys(data[0]).forEach(key => {
      maxWidths.push({
        wch: Math.max(
          key.length,
          ...data.map(row => String(row[key] || '').length)
        )
      });
    });
  }
  worksheet['!cols'] = maxWidths;
  
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

export const exportToPDF = (data, filename, title, columns = null) => {
  const doc = new jsPDF();
  
  // Add logo
  try {
    doc.addImage(logo, 'PNG', 14, 10, 20, 20);
  } catch (e) {
    console.log('Logo not found, skipping');
  }
  
  // Add title
  doc.setFontSize(16);
  doc.setTextColor(118, 0, 188); // #7600bc
  doc.text(title, 40, 20);
  
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 40, 28);
  
  // Prepare table data
  let tableData = [];
  let headers = [];
  
  if (columns) {
    headers = columns.map(col => col.header);
    tableData = data.map(row => columns.map(col => row[col.dataKey]));
  } else if (data.length > 0) {
    headers = Object.keys(data[0]);
    tableData = data.map(row => Object.values(row));
  }
  
  doc.autoTable({
    head: [headers],
    body: tableData,
    startY: 35,
    theme: 'grid',
    headStyles: {
      fillColor: [118, 0, 188], // #7600bc
      textColor: 255,
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9,
      textColor: [50, 50, 50]
    },
    alternateRowStyles: {
      fillColor: [245, 240, 250]
    }
  });
  
  doc.save(`${filename}.pdf`);
};

// Export payment report with study year grouping
export const exportPaymentReport = (payments, student, studyYears, filename) => {
  const doc = new jsPDF();

  const resolvePaymentTypeLabel = (payment) => {
    const rawType = payment.paymentTypeKey || payment.paymentType || 'oneTime';
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
      oneTime: 'One-time',
      enrollmentFee: 'Enrollment fee'
    };
    return labels[rawType] || rawType;
  };

  const resolveYearLabel = (payment) => {
    if (payment.schoolYearLabel) {
      return payment.schoolYearLabel;
    }

    const studyYear = studyYears.find(sy => String(sy.id) === String(payment.studyYearId));
    if (studyYear) {
      return `Year ${studyYear.year}`;
    }

    return payment.schoolYear || 'Unknown school year';
  };
  
  try {
    doc.addImage(logo, 'PNG', 14, 10, 20, 20);
  } catch (e) {
    console.log('Logo not found');
  }
  
  doc.setFontSize(16);
  doc.setTextColor(118, 0, 188);
  doc.text('Payment Statement', 40, 20);
  
  doc.setFontSize(12);
  doc.setTextColor(50);
  doc.text(`Student: ${student.name}`, 14, 40);
  doc.text(`Email: ${student.email}`, 14, 48);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 56);
  
  let currentY = 65;
  
  // Group payments by school year label.
  const paymentsByYear = {};
  payments.forEach(payment => {
    const yearKey = resolveYearLabel(payment);
    if (!paymentsByYear[yearKey]) {
      paymentsByYear[yearKey] = [];
    }
    paymentsByYear[yearKey].push(payment);
  });
  
  Object.keys(paymentsByYear).forEach(yearKey => {
    doc.setFontSize(12);
    doc.setTextColor(118, 0, 188);
    doc.text(yearKey, 14, currentY);
    currentY += 5;
    
    const yearPayments = paymentsByYear[yearKey];
    const tableData = yearPayments.map(p => [
      resolvePaymentTypeLabel(p),
      `SRD ${p.amount.toFixed(2)}`,
      `SRD ${p.paidAmount.toFixed(2)}`,
      `SRD ${(p.amount - p.paidAmount).toFixed(2)}`,
      p.status
    ]);
    
    doc.autoTable({
      head: [['Payment Type', 'Total', 'Paid', 'Remaining', 'Status']],
      body: tableData,
      startY: currentY,
      theme: 'grid',
      headStyles: {
        fillColor: [118, 0, 188],
        textColor: 255,
        fontSize: 9
      },
      bodyStyles: {
        fontSize: 8
      }
    });
    
    currentY = doc.lastAutoTable.finalY + 15;
  });
  
  // Add totals
  const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);
  const totalPaid = payments.reduce((sum, p) => sum + p.paidAmount, 0);
  
  doc.setFontSize(11);
  doc.setTextColor(50);
  doc.text(`Total Amount: SRD ${totalAmount.toFixed(2)}`, 14, currentY);
  doc.text(`Total Paid: SRD ${totalPaid.toFixed(2)}`, 14, currentY + 8);
  doc.text(`Remaining: SRD ${(totalAmount - totalPaid).toFixed(2)}`, 14, currentY + 16);
  
  doc.save(`${filename}.pdf`);
};

// Export payment report to Excel
export const exportPaymentReportExcel = (payments, student, studyYears, filename) => {
  const workbook = XLSX.utils.book_new();

  const resolvePaymentTypeLabel = (payment) => {
    const rawType = payment.paymentTypeKey || payment.paymentType || 'oneTime';
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
      oneTime: 'One-time',
      enrollmentFee: 'Enrollment fee'
    };
    return labels[rawType] || rawType;
  };

  const resolveYearLabel = (payment) => {
    if (payment.schoolYearLabel) {
      return payment.schoolYearLabel;
    }

    const studyYear = studyYears.find(sy => String(sy.id) === String(payment.studyYearId));
    if (studyYear) {
      return `Year ${studyYear.year}`;
    }

    return payment.schoolYear || 'Unknown school year';
  };
  
  // Summary sheet
  const summaryData = [
    ['Payment Statement'],
    [''],
    ['Student Name:', student.name],
    ['Student Email:', student.email],
    ['Generated:', new Date().toLocaleDateString()],
    [''],
    ['Total Amount:', `SRD ${payments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}`],
    ['Total Paid:', `SRD ${payments.reduce((sum, p) => sum + p.paidAmount, 0).toFixed(2)}`],
    ['Remaining:', `SRD ${(payments.reduce((sum, p) => sum + p.amount, 0) - payments.reduce((sum, p) => sum + p.paidAmount, 0)).toFixed(2)}`]
  ];
  
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
  
  // Group by school year label.
  const paymentsByYear = {};
  payments.forEach(payment => {
    const yearKey = resolveYearLabel(payment);
    if (!paymentsByYear[yearKey]) {
      paymentsByYear[yearKey] = [];
    }
    paymentsByYear[yearKey].push(payment);
  });
  
  // Create sheet for each year
  Object.keys(paymentsByYear).forEach(yearKey => {
    const yearPayments = paymentsByYear[yearKey];
    const sheetData = [
      ['Payment Type', 'Total Amount', 'Paid Amount', 'Remaining', 'Status'],
      ...yearPayments.map(p => [
        resolvePaymentTypeLabel(p),
        p.amount,
        p.paidAmount,
        p.amount - p.paidAmount,
        p.status
      ]),
      [],
      ['Subtotal', '', '', 
        yearPayments.reduce((sum, p) => sum + p.amount, 0),
        ''],
      ['Paid', '', '', 
        yearPayments.reduce((sum, p) => sum + p.paidAmount, 0),
        ''],
      ['Remaining', '', '', 
        yearPayments.reduce((sum, p) => sum + (p.amount - p.paidAmount), 0),
        '']
    ];
    
    const sheet = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(workbook, sheet, yearKey);
  });
  
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Export course scores report
export const exportCourseScoresReport = (course, students, grades, filename) => {
  const doc = new jsPDF();
  
  try {
    doc.addImage(logo, 'PNG', 14, 10, 20, 20);
  } catch (e) {}
  
  doc.setFontSize(16);
  doc.setTextColor(118, 0, 188);
  doc.text(`Course: ${course.name} (${course.code})`, 40, 20);
  
  doc.setFontSize(10);
  doc.setTextColor(50);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 35);
  
  const tableData = students.map(student => {
    const studentGrades = grades.filter(g => g.studentId === student.id && g.courseId === course.id);
    const avgScore = studentGrades.length > 0 
      ? studentGrades.reduce((sum, g) => sum + g.score, 0) / studentGrades.length 
      : 0;
    const passed = avgScore >= 55;
    
    return [
      student.name,
      student.email,
      avgScore.toFixed(1),
      passed ? 'Passed' : 'Failed',
      studentGrades.length
    ];
  });
  
  doc.autoTable({
    head: [['Student Name', 'Email', 'Average Score', 'Status', 'Exams Completed']],
    body: tableData,
    startY: 42,
    theme: 'grid',
    headStyles: {
      fillColor: [118, 0, 188],
      textColor: 255
    }
  });
  
  doc.save(`${filename}.pdf`);
};

export const exportCourseScoresExcel = (course, students, grades, filename) => {
  const data = students.map(student => {
    const studentGrades = grades.filter(g => g.studentId === student.id && g.courseId === course.id);
    const avgScore = studentGrades.length > 0 
      ? studentGrades.reduce((sum, g) => sum + g.score, 0) / studentGrades.length 
      : 0;
    
    return {
      'Student Name': student.name,
      'Email': student.email,
      'Average Score': avgScore.toFixed(1),
      'Status': avgScore >= 55 ? 'Passed' : 'Failed',
      'Exams Completed': studentGrades.length
    };
  });
  
  exportToExcel(data, filename, course.name);
};
